import { useMutation } from '@tanstack/react-query';
import React, { Context, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Nav } from './Nav';
import {
	ActionResponse,
	APIResponse,
} from '~src/api/routes/overrides/overrides.bulk-import.post';

interface BulkImportProps {}

export const BulkImport: React.FC<BulkImportProps> = () => {
	// file inputs cannot be controlled, so just doing everything uncontrolled
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const [apiResponse, setApiResponse] = useState<any | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { authFetch } = useAuth({ scopes: [] });

	const { mutate, isPending } = useMutation({
		mutationFn: async ({ file }: { file: File }) => {
			const response = await authFetch(`/api/overrides/bulk-import`, {
				method: 'POST',
				body: file,
			});
			if (!response.ok) {
				throw new Error(
					`Bulk import failed with error: ${response.status} - ${response.statusText}`
				);
			}
			return await response.json();
		},
		onError: (error: Error) => {
			if (error.message.includes('Unauthorized')) {
				setErrorMessage('Authentication failed, please log out and back in');
				return;
			}
			setErrorMessage(error.message);
			return;
		},
		onSuccess: (data: APIResponse) => {
			const successful = data.filter((item) => {
				const locale = Object.keys(item.locales)[0];
				return item.locales[locale].success;
			}).length;
			const total = data.length;
			if (successful < total) {
				setErrorMessage(
					`${total - successful} of ${total} lines failed to import`
				);
			}
			setApiResponse(data);
		},
	});

	const handleImport = () => {
		const file = fileInputRef.current?.files?.[0];
		if (!file || !file.name.endsWith('.csv')) {
			alert('Please provide a csv file');
			return;
		}

		setErrorMessage(null);
		mutate({ file });
	};

	return (
		<>
			<Nav />
			<main>
				<p>
					Download data from the Search/Translation Overrides page, update it
					offline, then re-import here. The CSV must include a header row with
					columns: <code>text</code>, <code>id</code>, <code>en-GB</code>, and
					one or more target locale columns (e.g. <code>es-ES</code>).{' '}
					<a href="/static/example-import.csv" download>Download example</a>
				</p>
				<form
					className="search-toolbar"
					onSubmit={(e) => {
						e.preventDefault();
						handleImport();
					}}
				>
					<input type="file" ref={fileInputRef} />
					<button type="submit" disabled={isPending}>
						{isPending ? 'Importing...' : 'Import'}
					</button>
				</form>
				{errorMessage && <p className="error">{errorMessage}</p>}
				{apiResponse && <ApiResponseTable data={apiResponse} />}
			</main>
		</>
	);
};

const ApiResponseTable = ({ data }: { data: APIResponse }) => {
	const maxLocales = data.reduce(
		(max, item) => Math.max(max, Object.keys(item.locales).length),
		0
	);
	return (
		<pre>
			<table>
				<thead>
					<tr>
						<th>Line</th>
						{Object.keys(data[0].locales).map((locale) => (
							<th key={locale}>{locale}</th>
						))}
						<th>Success</th>
					</tr>
				</thead>
				<tbody>
					{data.map((item) => (
						<tr key={item.line}>
							<td>{item.line}</td>
							{Array.from({ length: maxLocales }).map((_, index) => {
								const locale = Object.keys(item.locales)[index];
								return (
									<td key={index}>
										{locale ? item.locales[locale].message : ''}
									</td>
								);
							})}
							<td>
								{Object.values(item.locales).every((locale) => locale.success)
									? '✅'
									: '❌'}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</pre>
	);
};
